import { useState, useCallback, useRef, useEffect } from 'react';
import { message } from 'antd';
import { useNavigate } from 'react-router-dom';
import { WAREHOUSE_ROUTES } from 'constants/routes';
import { useIdentifyOrderMutation } from 'api/warehouse';
import { WAREHOUSE_ATTACHMENT_TYPE } from 'constants/index';
import { base64ToBlob, getMimeTypeFromBase64, getExtensionFromMimeType } from 'utils/helpers';

export const useUploadDeliveryNote = () => {
  const [images, setImages] = useState<string[]>([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [orderIdentifier, setOrderIdentifier] = useState<string>('');
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const hasCameraOpened = useRef(false);
  const touchStartX = useRef<number>(0);
  const touchEndX = useRef<number>(0);
  const navigate = useNavigate();
  const [identifyOrder, { isLoading }] = useIdentifyOrderMutation();

  // Detect if user is on mobile device
  const isMobile =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) &&
    !/Macintosh|Windows|Linux/i.test(navigator.userAgent);

  // Start camera when component mounts with no images (only for desktop)
  useEffect(() => {
    if (images.length === 0 && !isCameraActive && !isMobile) {
      startCamera();
    }
    return () => {
      stopCamera();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // On mobile, automatically open file input with camera on mount
  useEffect(() => {
    if (images.length === 0 && isMobile && !hasCameraOpened.current && fileInputRef.current) {
      hasCameraOpened.current = true;
      // Use requestAnimationFrame to ensure DOM is ready and try to preserve user gesture
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          fileInputRef.current?.click();
        });
      });
    }
  }, [images.length, isMobile]);

  // Update video element when stream changes
  useEffect(() => {
    if (stream && videoRef.current) {
      videoRef.current.srcObject = stream;
      videoRef.current.play().catch((err) => {
        console.error('Error playing video:', err);
      });
    }
  }, [stream]);

  const startCamera = useCallback(async () => {
    try {
      let mediaStream;
      try {
        // Try rear camera first (for mobile devices)
        mediaStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } },
          audio: false,
        });
      } catch {
        // Fallback to any available camera (for laptops)
        mediaStream = await navigator.mediaDevices.getUserMedia({
          video: { width: { ideal: 1920 }, height: { ideal: 1080 } },
          audio: false,
        });
      }
      setStream(mediaStream);
      setIsCameraActive(true);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        // Explicitly play the video
        videoRef.current.play().catch((err) => {
          console.error('Error playing video:', err);
        });
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
      message.error('Unable to access camera. Please check permissions.');
      setIsCameraActive(false);
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
    setIsCameraActive(false);
  }, [stream]);

  const handleFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        if (!file.type.startsWith('image/')) {
          message.error('Please select an image file');
          event.target.value = ''; // Reset input
          return;
        }

        const reader = new FileReader();
        reader.onloadend = () => {
          const base64String = reader.result as string;
          setImages((prev) => {
            const newImages = [...prev];
            newImages[currentImageIndex] = base64String;
            return newImages;
          });
          // Stop camera after image is selected from gallery
          stopCamera();
        };
        reader.readAsDataURL(file);
      }
      // Reset input value to allow selecting the same file again
      event.target.value = '';
    },
    [currentImageIndex, stopCamera]
  );

  const handleCapture = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    if (!context) return;

    // Set canvas dimensions to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw the current video frame to canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert canvas to base64 image
    const base64Image = canvas.toDataURL('image/jpeg', 0.9);

    // Add image to the current index
    setImages((prev) => {
      const newImages = [...prev];
      newImages[currentImageIndex] = base64Image;
      return newImages;
    });

    // Stop camera after capture
    stopCamera();
  }, [currentImageIndex, stopCamera]);

  const handleOpenGallery = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleRetake = useCallback(() => {
    // Clear current image and restart camera (or open file input on mobile)
    setImages((prev) => {
      const newImages = [...prev];
      newImages[currentImageIndex] = '';
      return newImages;
    });
    if (isMobile) {
      setTimeout(() => {
        fileInputRef.current?.click();
      }, 100);
    } else {
      startCamera();
    }
  }, [currentImageIndex, startCamera, isMobile]);

  const handleCaptureNextPage = useCallback(() => {
    if (images[currentImageIndex]) {
      setCurrentImageIndex((prev) => prev + 1);
      setImages((prev) => [...prev, '']);
      // Restart camera for next page (or open file input on mobile)
      if (isMobile) {
        setTimeout(() => {
          fileInputRef.current?.click();
        }, 100);
      } else {
        startCamera();
      }
    } else {
      message.warning('Please capture current page first');
    }
  }, [images, currentImageIndex, startCamera, isMobile]);

  const handlePrevious = useCallback(() => {
    if (currentImageIndex > 0) {
      setCurrentImageIndex((prev) => prev - 1);
    }
  }, [currentImageIndex]);

  const handleNext = useCallback(() => {
    if (currentImageIndex < images.length - 1) {
      setCurrentImageIndex((prev) => prev + 1);
    }
  }, [currentImageIndex, images.length]);

  // Touch event handlers for swipe gestures
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback(() => {
    const swipeDistance = touchStartX.current - touchEndX.current;
    const minSwipeDistance = 50; // Minimum distance for swipe to be recognized

    if (Math.abs(swipeDistance) > minSwipeDistance) {
      if (swipeDistance > 0) {
        // Swiped left - go to next image
        if (currentImageIndex < images.length - 1 && images[currentImageIndex + 1]) {
          handleNext();
        }
      } else {
        // Swiped right - go to previous image
        if (currentImageIndex > 0) {
          handlePrevious();
        }
      }
    }

    // Reset touch positions
    touchStartX.current = 0;
    touchEndX.current = 0;
  }, [currentImageIndex, images, handleNext, handlePrevious]);

  const handleDone = useCallback(async () => {
    const validImages = images.filter((img) => img !== '');

    if (validImages.length === 0) {
      message.error('Please capture at least one image');
      return;
    }

    try {
      setIsProcessing(true);

      // Convert base64 to files
      const files = validImages.map((base64Image, index) => {
        const blob = base64ToBlob(base64Image);
        const mimeType = getMimeTypeFromBase64(base64Image);
        const extension = getExtensionFromMimeType(mimeType);
        return new File([blob], `delivery-note-${index + 1}.${extension}`, { type: mimeType });
      });

      // Identify order - the API handles file upload internally
      const identifyFormData = new FormData();
      files.forEach((file) => {
        identifyFormData.append('image', file);
      });
      identifyFormData.append('attachmentType', WAREHOUSE_ATTACHMENT_TYPE);

      const identifyResponse = await identifyOrder(identifyFormData).unwrap();

      if (!identifyResponse?.success) {
        throw new Error('Failed to identify order');
      }

      const ordersData = identifyResponse?.data || [];

      // Handle different response scenarios
      if (Array.isArray(ordersData) && ordersData.length === 0) {
        // No orders found - navigate to no orders screen
        setIsProcessing(false);
        navigate('/warehouse/no-orders-found');
        return;
      }

      if (Array.isArray(ordersData) && ordersData.length > 1) {
        // Multiple possible orders - store in sessionStorage and navigate to possible orders screen
        sessionStorage.setItem('possibleOrders', JSON.stringify(ordersData));
        sessionStorage.setItem('deliveryNoteFiles', JSON.stringify({ files: validImages }));
        setIsProcessing(false);
        navigate(WAREHOUSE_ROUTES.possibleOrders);
        return;
      }

      // Single order match - files already uploaded by identify-order API
      const orderData = Array.isArray(ordersData) ? ordersData[0] : ordersData;
      const identifier = orderData?.orderNumber || orderData?.uuid || `ORD-${Date.now()}`;
      setOrderIdentifier(identifier);

      message.success('Delivery note uploaded successfully');

      // Navigate to order details page
      setTimeout(() => {
        setIsProcessing(false);
        const orderId = orderData?.id;
        if (orderId) {
          navigate(`${WAREHOUSE_ROUTES.inboundPackages}/${orderId}`);
        } else {
          navigate(WAREHOUSE_ROUTES.inboundPackages);
        }
      }, 1500);
    } catch (error) {
      console.error('Upload error:', error);
      message.error('Failed to upload delivery note. Please try again.');
      setIsProcessing(false);
    }
  }, [images, identifyOrder, navigate]);

  const handleCancel = useCallback(() => {
    stopCamera();
    navigate(WAREHOUSE_ROUTES.inboundPackages);
  }, [navigate, stopCamera]);

  return {
    images,
    currentImageIndex,
    isProcessing,
    isLoading,
    orderIdentifier,
    isCameraActive,
    isMobile,
    fileInputRef,
    videoRef,
    canvasRef,
    handleFileSelect,
    handleCapture,
    handleOpenGallery,
    handleRetake,
    handleCaptureNextPage,
    handlePrevious,
    handleNext,
    handleDone,
    handleCancel,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
  };
};
